/**
 * Retry policy.
 *
 * Three things about the API shape this module, and each is easy to get wrong from the outside.
 *
 * **A limit is reported, never counted against.** Every 429 of every surface names its wait in
 * `Retry-After`: the seconds left in a per-minute window, from 1 to 60, which the default budget
 * waits out; or, for a daily bound such as the organizations one user creates or the invitations
 * one organization sends, the seconds until the next day begins in UTC, up to 86400; or, for a
 * spent monthly allowance, the seconds until the next month begins in UTC. No budget a client
 * would set waits for a spent daily bound or monthly allowance, so that error arrives at once and
 * carries the wait. The figures an organization is held to are its agreement's, which may raise, lower or
 * remove them, so the client never tries to predict a 429, only to recover from one.
 *
 * **Every write carries a key, and the key is what makes a repeat safe.** A dropped socket does
 * not say whether a write landed. The API answers a repeated `Idempotency-Key` with the answer it
 * stored rather than running the write again, so a write that carried one is repeated after a
 * 502, a 503, a 504 or no answer at all, as an operation declaring itself idempotent is, and a 409
 * saying the key is still in use is asked again until the first attempt has finished, for as long
 * as the call's wait budget lasts: that repeat asks whether the first attempt is done, so it is
 * not counted against the attempts a failure may spend. A 412 is never repeated: the tag the write
 * named is stale, and what the write should be now is its caller's to decide.
 *
 * **Idempotence is not readable from the HTTP method.** The Data API is GraphQL over POST, and
 * every one of its operations is a query. A policy that refused to retry POSTs would give the
 * surface with the tightest limit no retries at all, which is exactly backwards. So idempotence
 * is declared by the operation, not inferred.
 */

import { KEY_IN_USE_CODE, refuseOption } from "./errors.js";

/** The public surfaces: the Data API, the Map Studio REST API and the Tile API. */
export type Surface = "rest" | "tiles" | "data";

/**
 * MAX_TIMER_DELAY_MS is the longest delay, in milliseconds, a timer honours: 2^31 - 1.
 *
 * Past it Node and the browsers fire the timer at once, so a delay or a timeout above it is not
 * a long wait but no wait at all. Every option that becomes a timer is held to it, and
 * {@link sleep} clamps to it.
 */
export const MAX_TIMER_DELAY_MS = 2 ** 31 - 1;

/**
 * RetryOptions configures the backoff. Defaults are deliberately modest: a client that retries
 * hard against a per-minute limit converts one slow minute into a longer one.
 *
 * Each member is checked where it is given, at construction for a client's policy and before
 * the first attempt for a call's, and a value outside its range throws a
 * `MountayaConfigurationError` naming the member and the value. `undefined` is an omitted
 * member, never a value.
 */
export interface RetryOptions {
  /**
   * Attempts after the first, as a whole number of 0 or more. `0` disables retrying.
   *
   * A repeat of a write whose `Idempotency-Key` is still in use (a 409 coded
   * `IDEMPOTENCY_KEY_IN_USE`) is not counted here: it asks again whether the first request with
   * the key has finished, and {@link RetryOptions.maxWaitMs} alone bounds it.
   *
   * @default 2
   */
  readonly maxRetries?: number | undefined;
  /**
   * Base delay in milliseconds for the exponential schedule, from 0 to 2,147,483,647.
   *
   * @default 500
   */
  readonly baseDelayMs?: number | undefined;
  /**
   * Ceiling in milliseconds for one delay the schedule draws, from 0 to 2,147,483,647.
   *
   * A `Retry-After` the response carries is not held to it: that wait is the server's to name,
   * and is held to {@link RetryOptions.maxWaitMs} instead.
   *
   * @default 30000
   */
  readonly maxDelayMs?: number | undefined;
  /**
   * The most one call waits between its attempts, all its waits together, in milliseconds from
   * 0 to 2,147,483,647.
   *
   * A wait is taken only where it fits what the waits before it left. One that does not fit is
   * not taken: the attempt settles with the error its status maps to, which on a 429 carries the
   * seconds the answer named for the caller to schedule from. A spent monthly allowance names a
   * wait past any budget a client would set, so it settles at once. It is the one bound on the
   * repeats of a write whose `Idempotency-Key` is still in use, each of which waits at least a
   * second, so a call that leaves it at `0` repeats none of them.
   *
   * @default 60000
   */
  readonly maxWaitMs?: number | undefined;
}

/** The policy a client takes where it names none, and what each member it omits keeps. */
export const defaultRetry: Required<RetryOptions> = {
  baseDelayMs: 500,
  maxDelayMs: 30_000,
  maxRetries: 2,
  maxWaitMs: 60_000,
};

/**
 * retryMember reads one member of an override, refusing a value outside its range.
 *
 * Returns undefined for an omitted member, which {@link resolveRetry} then takes from `policy`,
 * the policy the override is laid over and the value a refusal says an omission keeps.
 */
const retryMember = (
  override: RetryOptions,
  policy: Required<RetryOptions>,
  member: keyof RetryOptions,
  context: string | undefined,
): number | undefined => {
  const value: unknown = override[member];
  if (value === undefined) {
    return undefined;
  }

  if (member === "maxRetries") {
    // A count, not a timer, so it has no ceiling: a caller retrying for as long as the API
    // answers 429 names a large count rather than an infinite one.
    if (typeof value === "number" && Number.isInteger(value) && value >= 0) {
      return value;
    }

    throw refuseOption(
      `retry.${member}`,
      value,
      `pass a whole number of 0 or more, or leave it out to keep ${policy.maxRetries}.`,
      context,
    );
  }

  if (typeof value === "number" && value >= 0 && value <= MAX_TIMER_DELAY_MS) {
    return value;
  }

  throw refuseOption(
    `retry.${member}`,
    value,
    `pass a number of milliseconds from 0 to ${MAX_TIMER_DELAY_MS}, or leave it out to keep ${policy[member]}.`,
    context,
  );
};

/**
 * resolveRetry lays an override over a policy, member by member, refusing a member that cannot
 * work.
 *
 * The policy is configurable per client and per call, so a call's `retry` is laid over its
 * client's, and a client's over the defaults. Member by member, because an override naming only
 * `maxRetries` means "these delays, fewer attempts" rather than "no delays at all". And read
 * member by member rather than spread, because a spread copies a member the caller wrote as
 * `undefined` (which `RetryOptions` permits) over the value it was meant to leave alone, and
 * a `maxRetries` of `undefined` compares false against every attempt number, so the loop would
 * go on repeating a 429 without end.
 *
 * Checked here because every policy passes through here before it is read: a client's at
 * construction, and a call's before its first attempt. A `NaN` or infinite `maxRetries` never
 * reaches the ceiling, and a delay out of the timer's range fires at once, so each is refused
 * where it is given rather than discovered by a call that did nothing wrong.
 *
 * @param context - the call a per-call override was passed to, named in a refusal.
 * @throws `MountayaConfigurationError` for a member outside its range, or an override that is
 *   not an object.
 */
export const resolveRetry = (
  policy: Required<RetryOptions>,
  override: RetryOptions | undefined,
  context?: string,
): Required<RetryOptions> => {
  if (override === undefined) {
    return policy;
  }

  if (typeof override !== "object" || override === null) {
    throw refuseOption(
      "retry",
      override,
      "pass an object such as { maxRetries: 0 }, or leave it out to keep the policy as it is.",
      context,
    );
  }

  return {
    baseDelayMs: retryMember(override, policy, "baseDelayMs", context) ?? policy.baseDelayMs,
    maxDelayMs: retryMember(override, policy, "maxDelayMs", context) ?? policy.maxDelayMs,
    maxRetries: retryMember(override, policy, "maxRetries", context) ?? policy.maxRetries,
    maxWaitMs: retryMember(override, policy, "maxWaitMs", context) ?? policy.maxWaitMs,
  };
};

/**
 * transientStatuses are the statuses that say the API or a proxy failed in a way that is routinely
 * transient: 502, 503 and 504. Every other 4xx and 5xx is a statement about the request itself or
 * one no repeat changes, and repeating it unchanged produces the identical answer while spending
 * a limit to do so.
 */
const transientStatuses = new Set([502, 503, 504]);

/**
 * KEY_IN_USE_MIN_WAIT_MS is the least a repeat of a write whose key is still in use waits, in
 * milliseconds: the second the API names on every such answer. That repeat counts against no
 * ceiling of attempts and is bounded by the call's wait budget alone, which a wait of nothing
 * would never spend.
 */
export const KEY_IN_USE_MIN_WAIT_MS = 1000;

/**
 * isKeyInUse reports whether an answer is the 409 whose `Idempotency-Key` is still in use, for a
 * request that carried one: the first request with the key has not finished.
 */
export const isKeyInUse = (args: {
  /** Whether the request carried an `Idempotency-Key`. */
  readonly keyed?: boolean | undefined;
  readonly status?: number | undefined;
  /** The classifying code of the answer's first entry. */
  readonly code?: string | undefined;
}): boolean => args.keyed === true && args.status === 409 && args.code === KEY_IN_USE_CODE;

/**
 * shouldRetry decides whether an attempt may be repeated.
 *
 * - A 429 is repeated whatever the operation declares: the API has said it did nothing.
 * - A 502, a 503, a 504 and a request that never arrived are repeated where the operation
 *   declares itself idempotent or the request carried a key, which the API answers by replaying
 *   the answer it stored rather than running the write twice.
 * - A 409 coded `IDEMPOTENCY_KEY_IN_USE` is repeated where the request carried a key: the repeat
 *   carries the same key and is answered with whatever the first attempt is answered with. It is
 *   not one of the `retries` `maxRetries` bounds: the call's wait budget alone bounds it.
 * - Nothing else is repeated, a 412 least of all, and nothing past `maxRetries`.
 *
 * Whether the wait a repeat needs fits the call's budget is the loop's question, not this one's.
 */
export const shouldRetry = (args: {
  /** The repeats the call has made that count against `maxRetries`: all but a key in use. */
  readonly retries: number;
  readonly maxRetries: number;
  readonly idempotent: boolean;
  /** Whether the request carried an `Idempotency-Key`. */
  readonly keyed?: boolean | undefined;
  readonly status?: number | undefined;
  /** The classifying code of the answer's first entry, read for a 409 alone. */
  readonly code?: string | undefined;
}): boolean => {
  if (args.status === 409) {
    return isKeyInUse(args);
  }

  if (args.retries >= args.maxRetries) {
    return false;
  }

  const safe = args.idempotent || args.keyed === true;

  if (args.status === undefined) {
    return safe;
  }

  if (args.status === 429) {
    return true;
  }

  return safe && transientStatuses.has(args.status);
};

/**
 * RETRY_AFTER_JITTER_MS is the most a wait `Retry-After` names is drawn longer by, in
 * milliseconds: the clients one second refused do not all come back in the same millisecond.
 */
const RETRY_AFTER_JITTER_MS = 1000;

/**
 * jitteredDelay draws the next delay from the exponential schedule, in whole milliseconds.
 *
 * The draw is uniform over `[0, exponential]`, which is **full jitter**, rather than the
 * exponential itself. Fixed exponential backoff synchronises every client that failed at the
 * same moment into retrying at the same moment; full jitter is what breaks that up, and it is a
 * one-line difference for a large change in behaviour under load. A zero base stays zero at any
 * attempt, where the product would read `0 * Infinity` once the exponential overflows.
 */
export const jitteredDelay = (args: {
  readonly attempt: number;
  readonly baseDelayMs: number;
  readonly maxDelayMs: number;
  /** Injected for tests; defaults to Math.random. */
  readonly random?: (() => number) | undefined;
}): number => {
  const exponential =
    args.baseDelayMs === 0 ? 0 : Math.min(args.maxDelayMs, args.baseDelayMs * 2 ** args.attempt);

  return Math.round((args.random ?? Math.random)() * exponential);
};

/**
 * backoffDelay returns how long to wait before the next attempt, in whole milliseconds.
 *
 * `Retry-After` wins outright when the response carries one: the server is the only party that
 * knows when the window actually resets, and a client that backs off for less is simply
 * generating another 429. The wait is the seconds it names plus a uniform draw from zero to
 * {@link RETRY_AFTER_JITTER_MS}. Otherwise the delay is the schedule's draw, from
 * {@link jitteredDelay}. One draw is made per wait either way.
 *
 * Whether the wait fits what the call's budget has left is the caller's to judge.
 */
export const backoffDelay = (args: {
  readonly attempt: number;
  readonly retryAfterSeconds?: number | undefined;
  readonly baseDelayMs: number;
  readonly maxDelayMs: number;
  /** Injected for tests; defaults to Math.random. */
  readonly random?: (() => number) | undefined;
}): number => {
  if (args.retryAfterSeconds !== undefined) {
    return (
      args.retryAfterSeconds * 1000 +
      Math.round((args.random ?? Math.random)() * RETRY_AFTER_JITTER_MS)
    );
  }

  return jitteredDelay(args);
};

/**
 * namedError builds the error a runtime's own abort machinery would: a `DOMException` carrying
 * `name`, or, where the runtime has no `DOMException`, an `Error` carrying it.
 */
export const namedError = (name: "AbortError" | "TimeoutError", message: string): Error =>
  typeof DOMException === "function"
    ? new DOMException(message, name)
    : Object.assign(new Error(message), { name });

/**
 * abortReasonOf is what a caller's abort settles a call with: the signal's own reason, or, on a
 * runtime whose signals carry none (the polyfill React Native shipped before 0.87), the
 * `AbortError` fetch itself rejects with there.
 *
 * Every place a call can be waiting when its caller aborts (the request, a backoff, a body) settles
 * with this, so the value an aborted call rejects with does not depend on where it was.
 */
export const abortReasonOf = (signal: AbortSignal): unknown =>
  signal.reason ?? namedError("AbortError", "This operation was aborted");

/**
 * sleep resolves after `ms`, rejecting early if the signal aborts.
 *
 * A backoff that ignored the signal would keep a caller's abort waiting out the full
 * delay, which is the one moment a caller is most likely to be watching. The rejection is
 * {@link abortReasonOf} the signal. The timer is handed at most {@link MAX_TIMER_DELAY_MS}, since
 * past it a timer fires at once.
 */
export const sleep = (ms: number, signal?: AbortSignal): Promise<void> =>
  new Promise((resolve, reject) => {
    if (signal?.aborted === true) {
      reject(abortReasonOf(signal));

      return;
    }

    const timer = setTimeout(
      () => {
        signal?.removeEventListener("abort", onAbort);
        resolve();
      },
      Math.min(ms, MAX_TIMER_DELAY_MS),
    );

    const onAbort = () => {
      clearTimeout(timer);
      if (signal !== undefined) {
        reject(abortReasonOf(signal));
      }
    };

    signal?.addEventListener("abort", onAbort, { once: true });
  });
